import { format, parseISO, fromUnixTime, isValid } from 'date-fns';
import translator from '@/locale/translator';

export const PJAS_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';

function getLocaleDateFormat(key: string): string {
  return translator.get(key) || 'yyyy-MM-dd';
}

/**
 * Safely parse and format a date value
 * @param value - Date value as string, number, Date object, or null/undefined
 * @param formatString - Optional format string, defaults to locale format
 * @returns Formatted date string or fallback text
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  formatString: string | null = null
): string {
  // Handle null/undefined values
  if (value == null) {
    return 'Not set';
  }

  const effectiveFormat = formatString || getLocaleDateFormat('date.format');

  try {
    let dateToFormat: Date;

    if (value instanceof Date) {
      if (!isValid(value)) throw new Error('Invalid Date object');
      dateToFormat = value;
    } else if (typeof value === 'number') {
      dateToFormat = new Date(value);
      if (!isValid(dateToFormat)) throw new Error('Invalid timestamp');
    } else if (typeof value === 'string') {
      // Try parsing as ISO first
      dateToFormat = parseISO(value);
      if (!isValid(dateToFormat)) {
        // Fallback to Date constructor
        dateToFormat = new Date(value);
        if (!isValid(dateToFormat)) throw new Error('Invalid date string');
      }
    } else {
      throw new Error('Unsupported date value type');
    }

    return format(dateToFormat, effectiveFormat, {
      locale: window.__pjas_date_locale?.[window.__pjas_locale],
    });
  } catch (error) {
    console.warn('Date formatting error:', error, 'Value:', value);
    return 'Invalid Date';
  }
}

export function formatTimestampToDate(timestamp: number) {
  return fromUnixTime(timestamp);
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  formatString: string | null = null
): string {
  const effectiveFormat = formatString || getLocaleDateFormat('date.format_datetime');
  return formatDate(value, effectiveFormat);
}

export function formatDateTimeLong(
  value: string | number | Date | null | undefined,
  formatString: string | null = null
): string {
  const effectiveFormat = formatString || getLocaleDateFormat('date.format_datetime_long');
  return formatDate(value, effectiveFormat);
}

function dateFormatFilter(
  value: string | number | Date | null | undefined,
  formatString: string | null = null
): string {
  return formatDate(value, formatString);
}

export default dateFormatFilter;
